import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { Clock, ShieldAlert, HeartPulse, Activity, CheckCircle2, Thermometer, Trash2, Save } from "lucide-react";
import image1 from '../../Assets/Services/Root-canal1.jpg'
import image2 from '../../Assets/Services/Root-canal2.jpg'


const RootCanal = () => {
  return (
    <ServiceLayout
      title="Advanced single-visit root canal treatment"
      subtitle="Relieve tooth pain instantly and save your natural smile in just one comfortable visit."
      heroImage="/images/hero/root-canal.jpg"
      images={[image1, image2]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <p className="text-lg text-gray-700 leading-relaxed">
          Severe tooth pain caused by deep decay or infection can make everyday activities like eating and
sleeping uncomfortable. The good news is that modern endodontic treatments can effectively
remove infection and help save your natural tooth. One of the most advanced solutions available
today is the Single Visit Root Canal Treatment.
        </p>
        <p className="mt-4 text-gray-700">
          At Dr. Sridhar International Dental Hospitals, our experienced endodontic specialists provide
advanced root canal treatment in Vijayawada and Hyderabad using modern dental technology. Our
goal is to relieve your pain quickly and restore your natural smile in just one comfortable visit.
        </p>
      </section>

      {/* What is Endodontics? - Informational Box */}
      <section className="bg-blue-50 p-8 rounded-2xl mb-12 border border-blue-100">
        <h2 className="text-2xl font-bold text-blue-900 mb-4 flex items-center gap-2">
          <HeartPulse className="text-blue-600" /> What is Endodontics?
        </h2>
        <p className="text-gray-700 leading-relaxed">
          Endodontics is a specialized area of dentistry that focuses on treating problems inside the tooth,
particularly the dental pulp—the soft tissue that contains nerves and blood vessels. When this tissue
becomes infected due to deep decay, cracks, or injury, root canal treatment is performed to remove
the infection and save the natural tooth.
        </p>
                    <p>Root canal treatment helps:</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {[
            { icon: <Trash2 className="w-4 h-4 text-red-500" />, text: "Eliminate tooth infection" },
            { icon: <Activity className="w-4 h-4 text-red-500" />, text: "Relieve severe tooth pain" },
            { icon: <Save className="w-4 h-4 text-blue-500" />, text: "Preserve natural tooth structure" },
            { icon: <ShieldAlert className="w-4 h-4 text-blue-500" />, text: "Prevent the spread of infection" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 bg-white p-3 rounded-lg shadow-sm">
              {item.icon}
              <span className="text-sm font-semibold text-gray-700">{item.text}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-center font-bold text-blue-800">Saving your natural tooth is always the best solution whenever possible.</p>
      </section>

      {/* Single Visit Advantage */}
      <section className="mb-12">
        <h2 className="text-3xl font-bold text-blue-900 mb-6 text-center">Why a Single Visit?</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white border border-gray-100 p-6 rounded-xl shadow-sm hover:shadow-md transition text-center">
            <Clock className="w-10 h-10 text-blue-600 mx-auto mb-4" />
            <h4 className="font-bold mb-2">Immediate Pain Relief</h4>
            <p className="text-sm text-gray-600">The infected tissue is removed in the same appointment, providing quick relief.</p>
          </div>
          <div className="bg-white border border-gray-100 p-6 rounded-xl shadow-sm hover:shadow-md transition text-center">
            <CheckCircle2 className="w-10 h-10 text-blue-600 mx-auto mb-4" />
            <h4 className="font-bold mb-2">Fewer Dental Visits</h4>
            <p className="text-sm text-gray-600">The entire treatment is completed in one visit, saving valuable time.</p>
          </div>
          <div className="bg-white border border-gray-100 p-6 rounded-xl shadow-sm hover:shadow-md transition text-center">
            <ShieldAlert className="w-10 h-10 text-blue-600 mx-auto mb-4" />
            <h4 className="font-bold mb-2">Reduced Risk</h4>
            <p className="text-sm text-gray-600">Minimizes the chance of bacteria entering the tooth between multiple appointments.</p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="mb-12 bg-gray-50 p-8 rounded-3xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Single Visit Root Canal Involves:</h2>
        <div className="space-y-4">
          {[
            "Removal of infected pulp",
            "Cleaning and shaping of root canals",
            "Disinfection of the canal system",
            "Sealing of the root canal",
            "Preparation for final restoration",
          ].map((step, idx) => (
            <div key={idx} className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-100">
              <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">
                {idx + 1}
              </span>
              <p className="font-medium text-gray-700">{step}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Symptom Checklist */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Symptoms That May Require Treatment</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: <ShieldAlert className="text-red-500" />, text: "Severe tooth pain" },
            { icon: <Thermometer className="text-red-500" />, text: "Sensitivity to hot or cold" },
            { icon: <Activity className="text-red-500" />, text: "Swelling around the tooth" },
            { icon: <Activity className="text-red-500" />, text: "Gum infection near the tooth" },
            { icon: <ShieldAlert className="text-red-500" />, text: "Tooth discoloration" },
            { icon: <ShieldAlert className="text-red-500" />, text: "Pain while chewing" },
          ].map((symptom, idx) => (
            <div key={idx} className="flex items-center gap-3 p-4 bg-white rounded-lg border border-red-50 shadow-sm">
              {symptom.icon}
              <span className="font-medium text-gray-800 text-sm">{symptom.text}</span>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-gray-600 italic">
          Early treatment helps save the tooth and prevents complex infections.
        </p>
      </section>

      {/* Why Choose Us Footer */}
      <section className="border-t pt-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Advanced Endodontic Care</h2>
        <p className="text-gray-700 mb-4">
          At Dr. Sridhar International Dental Hospitals, our experienced endodontic specialists use advanced
technology and modern techniques to perform precise and comfortable root canal treatments.<br/>Our state-of-the-art facilities in Vijayawada and Hyderabad are designed to ensure high success
rates, faster recovery, and maximum patient comfort while saving your natural tooth.
        </p>
        <div className="inline-block bg-blue-900 text-white px-6 py-2 rounded-lg font-bold">
          Don’t ignore tooth pain—save your natural smile today.
        </div>
      </section>
    </ServiceLayout>
  );
};

export default RootCanal;